import { useCallback, useEffect, useState } from 'react';
import { Card, Drawer, Modal, FormField, TextInput, FormSelect, PageHeader, StatusBadge, SettingsSection } from '@/components/ui';
import { adminApi, type RoleRow, type RolesCatalog } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

/**
 * תפקידים דינמיים (RBAC): אין תפקידים דיפולטיביים עם הרשאות קבועות — בעל
 * המרכזיה מגדיר כאן לכל תפקיד אילו מסכים ואילו פעולות מותרים. תפקידי
 * מערכת: "בעלים" (נעול, הכל) ו"נציג" (ברירת מחדל לנציגים — ניתן לעריכה).
 * הרשאות פר-משתמש ספציפי נערכות בכרטיס הנציג במסך "נציגים".
 */

export function Roles({ embedded = false }: { embedded?: boolean } = {}) {
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [catalog, setCatalog] = useState<RolesCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<RoleRow | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoleRow | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    void Promise.all([adminApi.roles(), adminApi.rolesCatalog()])
      .then(([res, cat]) => {
        setRoles((res.data as RoleRow[]) ?? []);
        setCatalog(cat);
      })
      .catch(() => toast('שגיאה בטעינת תפקידים', 'danger'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(r: RoleRow) {
    try {
      await adminApi.deleteRole(r.id);
      toast('נמחק', 'ok');
      setDeleteTarget(null);
      load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה במחיקה', 'danger');
    }
  }

  const personaLabel = (p?: string) => (p === 'agent' ? 'ממשק נציג' : 'ממשק ניהול');

  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      <PageHeader
        title="תפקידים והרשאות"
        subtitle="כל התפקידים דינמיים — קבע לכל תפקיד אילו מסכים והרשאות יש לו"
        actions={<button type="button" className="btn primary" onClick={() => setEditor('new')}>+ תפקיד חדש</button>}
      />

      <Card padded={false}>
        {loading ? (
          <div style={{ padding: 'var(--pad-card)', color: 'var(--muted)' }}>טוען…</div>
        ) : (
          <div>
            {roles.map((r) => {
              const isOwner = r.slug === 'owner' && r.is_system;
              const screensCount = Object.keys(r.permissions ?? {}).length;
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px var(--pad-card)', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong>{r.name}</strong>
                      <span dir="ltr" style={{ color: 'var(--muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>{r.slug}</span>
                      <StatusBadge tone={r.persona === 'agent' ? 'info' : 'neutral'} size="sm">{personaLabel(r.persona)}</StatusBadge>
                      {r.is_system && <StatusBadge tone={isOwner ? 'warn' : 'ok'} size="sm">{isOwner ? 'מערכת — נעול' : 'מערכת — ניתן לעריכה'}</StatusBadge>}
                    </div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
                      {isOwner ? 'גישה מלאה לכל המערכת' : `${screensCount} מסכים מורשים`}
                      {' · '}{r.users_count ?? 0} משתמשים
                      {r.description ? ` · ${r.description}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {!isOwner && <button type="button" className="btn" onClick={() => setEditor(r)}>ערוך הרשאות</button>}
                    {!r.is_system && (
                      <button
                        type="button"
                        className="btn danger"
                        disabled={(r.users_count ?? 0) > 0}
                        title={(r.users_count ?? 0) > 0 ? 'יש משתמשים משויכים — העבר אותם לתפקיד אחר קודם' : undefined}
                        onClick={() => setDeleteTarget(r)}
                      >
                        מחק
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <AiReportsAccessCard roles={roles} />

      <Drawer
        isOpen={!!editor}
        onClose={() => setEditor(null)}
        title={editor === 'new' ? 'תפקיד חדש' : `עריכת ${(editor as RoleRow)?.name ?? ''}`}
        width={640}
      >
        {editor && catalog && (
          <RoleEditor
            role={editor === 'new' ? null : editor}
            catalog={catalog}
            onSaved={() => { setEditor(null); load(); }}
          />
        )}
      </Drawer>

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת תפקיד"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את התפקיד <strong>{deleteTarget.name}</strong>?</p>
        </Modal>
      )}
    </div>
  );
}

/** עורך תפקיד: פרטים + persona + מטריצת מסכים×צפייה/עריכה מהקטלוג. */
function RoleEditor({ role, catalog, onSaved }: { role: RoleRow | null; catalog: RolesCatalog; onSaved: () => void }) {
  const [name, setName] = useState(role?.name ?? '');
  const [slug, setSlug] = useState(role?.slug ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [persona, setPersona] = useState<'admin' | 'agent'>(role?.persona ?? 'admin');
  const [perms, setPerms] = useState<Record<string, string[]>>(role?.permissions ?? {});
  const [saving, setSaving] = useState(false);

  const screens = persona === 'agent' ? catalog.agent_screens : catalog.admin_screens;

  function toggle(screen: string, action: string) {
    setPerms((prev) => {
      const actions = new Set(prev[screen] ?? []);
      if (actions.has(action)) {
        actions.delete(action);
        // שלילת צפייה שוללת את כל המסך — כל פעולה גוררת צפייה, ולכן
        // השארת פעולה אטומית בלי view הייתה נראית שמורה ולא משפיעה.
        if (action === 'view') actions.clear();
      } else {
        actions.add(action);
        if (action !== 'view') actions.add('view');
      }
      const next = { ...prev };
      if (actions.size) next[screen] = [...actions]; else delete next[screen];
      return next;
    });
  }

  function setAll(on: boolean) {
    setPerms(on
      ? Object.fromEntries(Object.keys(screens).map((s) => [s, actionsFor(s)]))
      : {});
  }

  /** כל הפעולות של המסך: view/edit + הדגלים האטומיים שהשרת מכריז עליהם. */
  function actionsFor(screen: string): string[] {
    return ['view', 'edit', ...Object.keys(catalog.screen_actions?.[screen] ?? {})];
  }

  async function save() {
    if (!name.trim() || (!role && !slug.trim())) { toast('יש למלא שם ומזהה', 'danger'); return; }
    setSaving(true);
    try {
      if (role) {
        await adminApi.updateRole(role.id, { name: name.trim(), description: description || null, permissions: perms });
      } else {
        await adminApi.createRole({ slug: slug.trim(), name: name.trim(), description: description || null, persona, permissions: perms });
      }
      toast('נשמר', 'ok');
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשמירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <FormField label="שם התפקיד" required><TextInput value={name} onChange={(e) => setName(e.target.value)} /></FormField>
      {!role && (
        <>
          <FormField label="מזהה (slug)" required hint="אותיות באנגלית, ספרות ומקפים — לא ניתן לשינוי אחר-כך">
            <TextInput value={slug} onChange={(e) => setSlug(e.target.value)} dir="ltr" />
          </FormField>
          <FormField label="ממשק" hint="לאיזה ממשק ייכנסו משתמשים עם התפקיד הזה — ניהול או אפליקציית נציג.">
            <FormSelect value={persona} onChange={(e) => { setPersona(e.target.value as 'admin' | 'agent'); setPerms({}); }}>
              <option value="admin">ממשק ניהול</option>
              <option value="agent">ממשק נציג</option>
            </FormSelect>
          </FormField>
        </>
      )}
      <FormField label="תיאור"><TextInput value={description ?? ''} onChange={(e) => setDescription(e.target.value)} /></FormField>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>מסכים והרשאות ({personaHe(persona)})</strong>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn ghost" style={{ fontSize: 'var(--t-xs)' }} onClick={() => setAll(true)}>סמן הכל</button>
          <button type="button" className="btn ghost" style={{ fontSize: 'var(--t-xs)' }} onClick={() => setAll(false)}>נקה הכל</button>
        </div>
      </div>

      <div className="stack" style={{ gap: 2 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 16px', alignItems: 'center' }}>
          <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>מסך</strong>
          <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>צפייה</strong>
          <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>עריכה</strong>
        </div>
        {Object.entries(screens).map(([screen, label]) => {
          const atomic = catalog.screen_actions?.[screen] ?? {};
          return (
            <div key={screen} style={{ padding: '2px 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 16px', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--t-sm)' }}>{label}</span>
                {(['view', 'edit'] as const).map((action) => (
                  <input
                    key={`${screen}-${action}`}
                    type="checkbox"
                    aria-label={`${label} — ${action === 'view' ? 'צפייה' : 'עריכה'}`}
                    checked={(perms[screen] ?? []).includes(action)}
                    onChange={() => toggle(screen, action)}
                  />
                ))}
              </div>
              {/* פעולות אטומיות (פרק 4.4): חייבות להיות מוצגות, אחרת שמירה
                  מהטופס תמחק אותן — כולל שלוש הרשאות הפיקוח. */}
              {Object.keys(atomic).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', padding: '2px 14px 6px' }}>
                  {Object.entries(atomic).map(([action, actionLabel]) => (
                    <label key={`${screen}-${action}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                      <input
                        type="checkbox"
                        aria-label={`${label} — ${actionLabel}`}
                        checked={(perms[screen] ?? []).includes(action)}
                        onChange={() => toggle(screen, action)}
                      />
                      {actionLabel}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור'}</button>
      </div>
    </div>
  );
}

function personaHe(p: 'admin' | 'agent'): string {
  return p === 'agent' ? 'ממשק נציג' : 'ממשק ניהול';
}

/**
 * מי רואה את דוחות ה-AI במסך "תובנות מוקד" — הבעלים בוחר תפקידים.
 * בעלים תמיד מורשה; העריכה זמינה לבעלים בלבד (can_edit מהשרת).
 */
function AiReportsAccessCard({ roles }: { roles: RoleRow[] }) {
  const [access, setAccess] = useState<{ allowed_roles: string[]; assignable_roles: string[]; can_edit: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  const roleLabel = (slug: string) => roles.find((r) => r.slug === slug)?.name ?? slug;

  useEffect(() => {
    void adminApi.aiReportAccess().then(setAccess).catch(() => {});
  }, []);

  // הגנה על תשובה חלקית/ריקה (וגם על ה-mock הגנרי של ה-e2e).
  if (!access || !Array.isArray(access.assignable_roles) || access.assignable_roles.length === 0) return null;
  const allowedRoles = Array.isArray(access.allowed_roles) ? access.allowed_roles : [];

  async function toggle(role: string, checked: boolean) {
    // מחשבים מהמצב העדכני (לא מה-closure) כדי ששתי לחיצות מהירות לא ידרסו.
    const current = Array.isArray(access?.allowed_roles) ? access!.allowed_roles : [];
    const next = checked ? [...new Set([...current, role])] : current.filter((r) => r !== role);
    setAccess((a) => (a ? { ...a, allowed_roles: next } : a));
    setSaving(true);
    try {
      await adminApi.updateAiReportAccess(next);
      toast('נשמר', 'ok');
    } catch {
      toast('שגיאה בשמירה', 'danger');
      setAccess((a) => (a ? { ...a, allowed_roles: current } : a)); // rollback
    } finally {
      setSaving(false);
    }
  }

  return (
    <SettingsSection
      title="דוחות AI — תובנות מוקד"
      icon="ai"
      selfSaving
      description={<>אילו תפקידים רואים את דוחות ה-AI היומיים (בעלים תמיד רואה).{!access.can_edit && ' — רק בעלים יכול לשנות.'}</>}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {access.assignable_roles.map((role) => (
          <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)', opacity: access.can_edit ? 1 : 0.7 }}>
            <input
              type="checkbox"
              checked={allowedRoles.includes(role)}
              disabled={!access.can_edit || saving}
              onChange={(e) => toggle(role, e.target.checked)}
            />
            {roleLabel(role)}
          </label>
        ))}
      </div>
    </SettingsSection>
  );
}
